import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { BsThreeDotsVertical } from "react-icons/bs";
import { ExportButton } from "admin/ui/base";
import { IPaginateRequest, IUser, IUserCourse, IUserQuiz, IUserStatus } from "utils/types";
import { Loader } from "common";
import { professionOptions } from "../Users/data";
import { Spin } from "antd";
import { ProgressCard } from "dashboard/ui/pages/Progress/ProgressCard";
import { AdminUserProgressCard, AdminUserProgressCardProps } from "./AdminUserProgressCard";


// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 40px;
  gap: 24px;
  font-family: "Manrope", sans-serif;
  background-color: #f8f9fa;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #5575f7;
  font-family: "Manrope", sans-serif;
  padding: 0;
  margin-right: 16px;
`;

const UserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #5575f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: 500;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.h2`
  font-family: "Manrope-Medium", sans-serif;
  font-size: 24px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserRole = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #666;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 10;
  min-width: 180px;
`;

const DropdownItem = styled.div<{ danger?: boolean }>`
  padding: 12px 16px;
  font-size: 14px;
  color: ${(props) => (props.danger ? "#CC0A3E" : "#333")};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const DotsContainer = styled.div`
  position: relative;
  cursor: pointer;
`;

const UserLocation = styled.div`
  font-size: 14px;
  color: #666;
`;

const StatusBadge = styled.span<{ status: "active" | "suspended" }>`
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 12px;
  font-family: "Manrope-Medium", sans-serif;
  background-color: ${(props) =>
    props.status === "active" ? "#e7f7e8" : "#FFF6E5"};
  color: ${(props) => (props.status === "active" ? "#1f9d55" : "#F59E0B")};
`;

const TabContainer = styled.div`
  margin-top: 20px;
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
`;

const TabHeader = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const Tab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  padding: 8px 16px;
  font-family: Manrope;
  ${({ active }) => active && `
    font-family: "Manrope-SemiBold";
  `}
  font-size: 16px;
  cursor: pointer;
  color: ${(props) => (props.active ? "#5575F7" : "#666")};
  background-color: ${(props) => (props.active ? "#F6F8FF" : "")};
  transition: all 0.2s ease;
  border-radius: 16px;
`;

const CourseCard = styled.div`
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  width: 600px;
  border: 1px solid var(--Grays-Gray-6, #f2f2f7);

  @media (max-width: 468px) {
    width: 250px;
  }
`;

const CourseType = styled.div`
  display: inline-block;
  padding: 4px 12px;
  background-color: #5575f7;
  color: white;
  border-radius: 100px;
  font-size: 14px;
  font-family: "Manrope-Medium", sans-serif;
  margin-bottom: 12px;
  text-transform: capitalize;
`;

const CourseTitle = styled.h3`
  font-family: "Manrope-Medium", sans-serif;
  font-size: 18px;
  margin: 0 0 12px 0;
`;

const CourseDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: #666;
`;

const DetailValue = styled.span`
  font-family: "Manrope-Medium", sans-serif;
  font-size: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 14px;
  background-color: #e1e4e8;
  border-radius: 10px;
  margin-top: 8px;
  margin-bottom: 8px;
  overflow: hidden;
`;

const Progress = styled.div<{ value: number }>`
  height: 100%;
  background-color: #5575f7;
  border-radius: 10px;
  width: ${(props) => props.value}%;
`;

const ScoreDisplay = styled.div`
  font-family: "Manrope-Medium", sans-serif;
  font-size: 16px;
  margin-top: 8px;
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  // max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h3`
  font-family: "Manrope-Medium", sans-serif;
  font-size: 18px;
  margin: 0;
  text-align: center;
`;

const ModalText = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: none;
  font-family: "Manrope-Medium", sans-serif;
  font-size: 14px;
  cursor: pointer;
  background-color: ${(props) => (props.primary ? "#5575F7" : "#F0F2F5")};
  color: ${(props) => (props.primary ? "white" : "#333")};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #6c757d;
`;
const QuizList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 700px;
`;
export interface UserProps extends Pick<AdminUserProgressCardProps, "onGetCourse"|"onGetCourseModuleCount"> {
  userId: string;
  user?: IUser
  onBack: () => void;
  onGetUser: (userId: string) => Promise<IUser|undefined>;
  onChangeUserStatus: (userId: string, status: IUserStatus) => Promise<IUser|undefined>;
  onGetUserCourses: (data: { userCourse?: Partial<IUserCourse>} & IPaginateRequest) => Promise<IUserCourse[]>;
  onGetUserQuizzes: (data: {userQuiz?: Partial<IUserQuiz>} & IPaginateRequest) => Promise<IUserQuiz[]>;
}
export const User: React.FC<UserProps> = ({
  user: initUser, userId, onGetUser, onChangeUserStatus,
  onGetUserCourses, onBack, onGetCourse, onGetUserQuizzes,
  onGetCourseModuleCount
}) => {

  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState<IUser|undefined>(initUser)
  const [userCourses, setUserCourses] = useState<IUserCourse[]>([])
  const [userQuizzes, setUserQuizzes] = useState<IUserQuiz[]>([])

  const [activeTab, setActiveTab] = useState<"in-progress" | "completed">(
    "in-progress"
  );
  const [updating, setUpdating] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginateRequest>({
    skip: 0, limit: 100
  })
  const dropdownRef = useRef<HTMLDivElement>(null);

    // userQuizzes and unFinishedQuizzes and userQuizzes together
  const data = [
    ...userQuizzes.map(item => ({...item, isUserQuiz: true})), 
    ...userCourses.map(item => ({...item, isUserQuiz: false})),
  ]
  // sort by createdAt
  data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  console.log({ user, userId })

  useEffect(() => {
    if(userId){
      onGetUser(userId).then(setUser)
    }
  }, [userId])
   useEffect(() => {
      (async () => {    
        setLoading(true);  
        switch(activeTab){
          case "in-progress": {
            const userCourses = await onGetUserCourses({ 
              userCourse: {
                userId, completed: "false" 
              }, ...pagination});
            setUserCourses(userCourses)
            setUserQuizzes([])
            break;
          }
          case "completed": {
            const [userCourses, userQuizzes] = await Promise.all([
              onGetUserCourses({ userCourse: {
                userId, completed: "true"
              }, ...pagination}),
              onGetUserQuizzes({ userQuiz: {
                userId, completed: "true" 
              }, ...pagination})
            ])
            setUserCourses(userCourses);
            setUserQuizzes(userQuizzes)
            break;
          }
        }
        setLoading(false)
      })();
    }, [userId, activeTab, JSON.stringify(pagination)])
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  
  const userInitial = `${user?.firstName?.charAt(0)}${user?.lastName?.charAt(0)}`;
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`;
  const isActive = user?.userStatus === "active";


  if(!user){
    return <Loader/>
  }
  // Close dropdown when clicking outside
  

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <IoArrowBack size={20} />
          Users
        </BackButton>
        <ExportButton />
      </Header>

      <UserCard>
        <UserHeader>
          <UserInfo>
            <Avatar>{userInitial}</Avatar>
            <UserDetails>
              <UserName>
                {fullName}
                <StatusBadge status={user.userStatus}>
                  {user.userStatus}
                </StatusBadge>
              </UserName>
              <UserRole>{professionOptions.find((option) => option.value === user.profession)?.label || user.profession}</UserRole>
              <UserLocation>{`${user.gender}, ${user.country}`}</UserLocation>
            </UserDetails>
          </UserInfo>
          <DotsContainer ref={dropdownRef}>
            <BsThreeDotsVertical size={16} onClick={e => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }} />
            {showDropdown && (
              <DropdownMenu>
                <DropdownItem danger onClick={() => {
                  setShowDropdown(false)
                  setShowModal(true)
                }}>
                  {user.userStatus === "suspended" ? "Unsuspend" : "Suspend"} User
                </DropdownItem>
              </DropdownMenu>
            )}
          </DotsContainer>
        </UserHeader>
      </UserCard>

      <TabContainer>
        <CourseTitle>Courses</CourseTitle>

        <TabHeader>
          <Tab
            active={activeTab === "in-progress"}
            onClick={() => setActiveTab("in-progress")}
          >
            In-progress
          </Tab>
          <Tab
            active={activeTab === "completed"}
            onClick={() => setActiveTab("completed")}
          >
            Completed
          </Tab>
        </TabHeader>

        {/* {data.map((item, index) => {
          const course = item.type === "quiz" ? (item as IUserQuiz)?.quiz?.course : (item as IUserCourse)?.course
          const userQuiz = item.type === "quiz" ? (item as IUserQuiz) : null 
          const userCourse = item.type === "course" ? (item as IUserCourse) : null
          return (
            <CourseCard key={index}>
              <CourseType>{course?.courseStudyType}</CourseType>
              <CourseTitle>{course?.title}</CourseTitle>

              {item.type === "quiz" && (
                <CourseDetail>
                  <DetailLabel>Score:</DetailLabel>
                  <ScoreDisplay>{item.score}</ScoreDisplay>
                  <DetailLabel>Quiz mode:</DetailLabel>
                  <DetailValue>{userQuiz?.quizMode}</DetailValue>
                </CourseDetail>
              )}

              {item.type === "course" && (
                <CourseDetail>
                  <DetailLabel>Progress:</DetailLabel>
                  <ProgressBar>
                    <Progress value={(item as IUserCourse).progress || 0} />
                  </ProgressBar>
                  <DetailLabel>Quiz mode:</DetailLabel>
                  <DetailValue>{course.mode}</DetailValue>
                </CourseDetail>
              )}
            </CourseCard>
          )
        })} */}
        {data.length === 0 ? 
          <EmptyState>
            No quizzes found.
          </EmptyState> :
          <QuizList>
            {data.map((quiz, index) => {
              return (
                <AdminUserProgressCard key={`quiz-${index}-${quiz.id}`} {...{
                  courseId: quiz.isUserQuiz ? 
                    (quiz as IUserQuiz).quiz?.courseId || "" : 
                    (quiz as IUserCourse).courseId || "",
                  data: quiz,
                  onGetCourse,
                  onGetCourseModuleCount
                }}/>
              )}
            )} 
          </QuizList>}
      </TabContainer>

      {showModal && (
        <ConfirmationModal>
          <ModalContent>
            <ModalTitle>{isActive ? "Suspend" : "Unsuspend"} this user?</ModalTitle>
            <ModalText>
              {isActive ? "Suspend" : "Unsuspend"}ing this user will {isActive ? "suspend" : "unsuspend"} their access to the platform.
            </ModalText>
            <ModalActions>
              <ModalButton onClick={() => {
                setShowModal(false);
              }}>No</ModalButton>
              <ModalButton disabled={updating} primary onClick={() => {
                 setUpdating(true);
                 onChangeUserStatus(user.id, isActive ? "suspended" : "active").
                 then(user => {
                   if(user) setUser(user)
                 }).finally(() => {
                  setUpdating(false)
                  setShowModal(false)
                })
              }}>
                {updating && <Spin/>}
                Yes, {`${!isActive ? "unsuspend" : "suspend"}`} user
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ConfirmationModal>
      )}
    </Container>
  );
};

export default User;
